import { Component, computed, inject, input } from '@angular/core';

import { RatingScalePresentation } from '@core/candidate-evaluation-settings-types';
import type { RatingScale } from '@core/candidate-evaluation-settings-types';
import { getRatingScoreLabel, getRatingThumb } from '@core/evaluations-util';
import { I18nService } from '@core/i18n-legacy';
import { IconComponent, IconSizes } from '@core/user-interface-legacy';

export type EvaluationRatingScoreFontSize = 'small-xx' | 'small-x' | 'small' | 'default' | 'medium';
export type EvaluationRatingScoreFontWeight = 'default' | 'medium' | 'semi-bold' | 'bold';
export type EvaluationRatingScoreTextColor = 'default' | 'strong' | 'low';

const THUMBS_PRESENTATIONS = new Set([
  RatingScalePresentation.Thumbs4,
  RatingScalePresentation.Thumbs5,
]);

@Component({
  selector: 'rt-evaluation-rating-score',
  templateUrl: './evaluation-rating-score.component.html',
  styleUrl: './evaluation-rating-score.component.less',
  host: {
    '[class.vertical]': "orientation() === 'vertical'",
  },
  imports: [IconComponent],
})
export class EvaluationRatingScoreComponent {
  private readonly i18n = inject(I18nService);

  public readonly evaluationScale = input.required<RatingScale>();
  public readonly averageRatingScorePercent = input<number>();
  public readonly score = input<number>();
  public readonly showRatingScoreLabel = input<boolean>(true);
  public readonly showIcon = input<boolean>(true);
  public readonly iconSize = input<keyof typeof IconSizes>('medium');
  public readonly colorize = input<boolean>(true);
  public readonly fontSize = input<EvaluationRatingScoreFontSize>('small-xx');
  public readonly fontWeight = input<EvaluationRatingScoreFontWeight>('bold');
  public readonly textColor = input<EvaluationRatingScoreTextColor>('default');
  public readonly orientation = input<'horizontal' | 'vertical'>('horizontal');

  protected labelClass = computed(
    () =>
      `score-label font-size-${this.fontSize()} font-weight-${this.fontWeight()} color-${this.textColor()}`,
  );

  protected ratingScore = computed(() => {
    const scale = this.evaluationScale();
    const rawScore = this.score();
    const percent = rawScore
      ? ((rawScore - 1) / (scale.range - 1)) * 100
      : this.averageRatingScorePercent();

    const thumbOption = getRatingThumb(percent ?? 50, scale.presentation);
    const { icon, color, translationKey } = thumbOption;

    let ratingLabel: string;
    if (percent == null) {
      ratingLabel = '—';
    } else if (rawScore) {
      ratingLabel = THUMBS_PRESENTATIONS.has(scale.presentation)
        ? this.i18n.translate(translationKey)
        : `${rawScore}/${scale.range}`;
    } else {
      ratingLabel = getRatingScoreLabel(percent, scale);
    }

    return { icon, color, ratingLabel };
  });
}
